import { createFileRoute, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useEffect, useRef, useState } from 'react';
import { useLoginTracking } from '@/hooks/useLoginTracking';
import { useTrackingParams } from '@/hooks/useTrackingParams';
import { EventName } from '@/lib/constants';
import { resolveLoginTarget } from '@/lib/previewHost';
import { InformEmailForm } from './-components/InformEmailForm';
import { InformLoginFlow } from './-components/InformLoginFlow';
import { LoginLayout } from './-components/LoginLayout';
import { Flex } from '@televet/kibble-ui/chakra';

const informLoginSearchSchema = z
  .object({
    emailAddress: z.string().optional(),
    // Opt-in override that lands the authenticated user in astro's own shell
    // instead of handing them to Flow. Engineer/QA affordance — nothing links to
    // it — so an unrecognised value falls back to the Flow default rather than
    // erroring the route.
    shell: z.enum(['flow', 'astro']).optional(),
    otto_partner: z.string().optional(),
    otto_source: z.string().optional(),
    otto_version: z.coerce.string().optional(),
    otto_content: z.string().optional(),
  })
  .passthrough();

export const Route = createFileRoute('/login/')({
  component: InformLoginPage,
  validateSearch: informLoginSearchSchema,
});

function InformLoginPage() {
  const search = useSearch({ from: '/login/' });
  const [emailAddress, setEmailAddress] = useState<string | undefined>(search.emailAddress);
  const { track } = useLoginTracking();
  const hasTrackedViewRef = useRef(false);
  useTrackingParams(search);

  // Fire the funnel-entry event once on mount. The tracking provider buffers
  // events until initialization completes, so firing from a mount effect is safe.
  useEffect(() => {
    if (hasTrackedViewRef.current) return;
    hasTrackedViewRef.current = true;
    track(EventName.INFORM_LOGIN_VIEWED, { surface: 'login' });
  }, [track]);

  return (
    <LoginLayout>
      <Flex direction="column" align="flex-start" height="100%">
        <Flex flex="1" direction="column" justify="center">
          {emailAddress ? (
            <InformLoginFlow
              emailAddress={emailAddress}
              target={resolveLoginTarget(search.shell)}
              onResetEmail={() => setEmailAddress(undefined)}
            />
          ) : (
            <InformEmailForm defaultEmail={search.emailAddress} onEmailSubmit={setEmailAddress} />
          )}
        </Flex>
      </Flex>
    </LoginLayout>
  );
}
